import React, { useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"
import styled from "styled-components"
import { useContract } from "~/composable/useContract"
import AcButton from "~/components/atoms/AcButton"
import ContentInner from "~/components/atoms/ContentInner"
import ListView from "~/components/molecules/ListView"
import dayjs from "dayjs"
import { useGlobal } from "~/composable/useGlobal"
import { useDispatch, useSelector } from "react-redux"
import { selectAuthorization, setIsRegisterTemplateForm } from "~/slices/authorizationSlice"
import ActivityView from "~/components/molecules/ActivityView"
import TaskView from "~/components/molecules/TaskView"
import { useRequest } from "~/api/Request"
import { AgentSummary, Summary } from "~/model/Summary"
import TaskTag from "~/components/molecules/TaskTag"
import Link from "next/link"
import { useConfig } from "~/composable/useConfig"
import { useFormClear } from "~/composable/useFormStore"
import { CompanyType } from "~/model/Company"
import ModalImportCSV from "~/components/organisms/ModalImportCSV"
import { FormDefault } from "~/model/Form"
import AcLoading from "~/components/atoms/AcLoading"
import AcInput from "~/components/atoms/AcInput"
import { FormProvider, useForm } from "react-hook-form"
import { useActivity } from "~/composable/useActivity"
import { useSummary } from "~/composable/useSummary"
import { useTask } from "~/composable/useTask"
import { useForms } from "~/composable/useForm"

export default function Dashboard() {
  const checkLoading = useRef(false)
  const dispatch = useDispatch()
  const authorization = useSelector(selectAuthorization)
  const router = useRouter()
  const { Request } = useRequest()
  const config = useConfig()
  const { setTitle, socket, loading, showDialog } = useGlobal()
  const { clearForm } = useFormClear()
  setTitle("TOP")
  const { tabData } = useContract()
  const methods = useForm()
  const { getActivityData } = useActivity()
  const { getSummaryData, getAgentSummaryData } = useSummary()
  const { getTaskData } = useTask()
  const { getDefaultForm } = useForms()

  const contractStatus = config.getConfigKeyValue("contractStatus")

  // 最近のアクティビティ
  const [activityData, setActivityData] = useState([])

  // 未完了の個人タスク
  const [taskData, setTaskData] = useState([])

  const tableHeaders = useMemo(() => {
    return {
      0: {
        title: "契約準備中",
        right: "契約準備中",
        theme: "light",
        headers: [
          {
            key: "name",
            title: "契約者名",
            render(data, alldata) {
              return (
                <Link
                  href={{
                    pathname: `/contracts${alldata.version === 2 ? "-next" : ""}/[id]/`,
                    query: { id: alldata.id },
                  }}
                >
                  {data}
                </Link>
              )
            },
            width: "25%",
          },
          { key: "propertyFullName", title: "物件名・部屋番号", width: "25%" },
          {
            key: "meetingDate",
            title: "重説予定日時",
            render(data) {
              return data ? dayjs(data).format("YYYY-MM-DD") : "未定"
            },
            width: "25%",
          },
          {
            key: "status",
            title: "ステータス",
            render(data) {
              return <TaskTag status={data} size={"fix"} />
            },
            width: "25%",
          },
        ],
      },
      30: {
        title: "契約済",
        right: "契約済",
        theme: "light",
        headers: [
          {
            key: "name",
            title: "契約者名",
            render(data, alldata) {
              return (
                <Link
                  href={{
                    pathname: `/contracts${alldata.version === 2 ? "-next" : ""}/[id]/`,
                    query: { id: alldata.id },
                  }}
                >
                  {data}
                </Link>
              )
            },
            width: "25%",
          },
          { key: "propertyFullName", title: "物件名・部屋番号", width: "25%" },
          {
            key: "importantNote.meetingDate",
            title: "契約締結日",
            render(data) {
              return data ? dayjs(data).format("YYYY-MM-DD") : ""
            },
            width: "25%",
          },
          {
            key: "status",
            title: "ステータス",
            render(data) {
              return <TaskTag status={data} size={"fix"} />
            },
            width: "25%",
          },
        ],
      },
      40: {
        title: "契約完了",
        right: "契約完了",
        theme: "dark",
        headers: [
          {
            key: "name",
            title: "契約者名",
            render(data, alldata) {
              return (
                <Link
                  href={{
                    pathname: `/contracts${alldata.version === 2 ? "-next" : ""}/[id]/`,
                    query: { id: alldata.id },
                  }}
                >
                  {data}
                </Link>
              )
            },
            width: "25%",
          },
          { key: "propertyFullName", title: "物件名・部屋番号", width: "25%" },
          {
            key: "contractorSignedAt",
            title: "契約完了日時",
            render(data) {
              const time = data
              return time ? (
                <>
                  {dayjs(time).format("YYYY/MM/DD")}
                  <br />
                  {dayjs(time).format("HH:mm")}
                </>
              ) : (
                <span style={{ color: "red" }}>-</span>
              )
            },
            width: "25%",
          },
          {
            key: "status",
            title: "ステータス",
            render(data) {
              return <TaskTag status={data} size={"fix"} />
            },
            width: "25%",
          },
        ],
      },
      50: {
        title: "キャンセル",
        right: "キャンセル",
        theme: "dark",
        headers: [
          {
            key: "name",
            title: "契約者名",
            render(data, alldata) {
              return (
                <Link
                  href={{
                    pathname: `/contracts${alldata.version === 2 ? "-next" : ""}/[id]/`,
                    query: { id: alldata.id },
                  }}
                >
                  {data}
                </Link>
              )
            },
            width: "25%",
          },
          { key: "propertyFullName", title: "物件名・部屋番号", width: "25%" },
          {
            key: "meetingDate",
            title: "重説予定日時",
            render(data) {
              return data ? dayjs(data).format("YYYY-MM-DD") : "未定"
            },
            width: "25%",
          },
        ],
      },
    }
  }, [])

  const agentTableHeaders = useMemo(() => {
    return {
      contractsConfirmation: {
        title: "未受領確認",
        theme: "light",
        headers: [
          {
            key: "name",
            title: "契約者名",
            render(data, alldata) {
              return (
                <Link
                  href={{
                    pathname: `/contracts${alldata.version === 2 ? "-next" : ""}/[id]/`,
                    query: { id: alldata.id },
                  }}
                >
                  {data}
                </Link>
              )
            },
            width: "25%",
          },
          { key: "propertyFullName", title: "物件名・部屋番号", width: "50%" },
          {
            key: "status",
            title: "ステータス",
            render(data) {
              return <TaskTag status={data} size={"fix"} />
            },
            width: "25%",
          },
        ],
      },
      contractsRequest: {
        title: "未承認",
        theme: "light",
        headers: [
          {
            key: "name",
            title: "契約者名",
            render(data, alldata) {
              return (
                <Link
                  href={{
                    pathname: `/contracts${alldata.version === 2 ? "-next" : ""}/[id]/`,
                    query: { id: alldata.id },
                  }}
                >
                  {data}
                </Link>
              )
            },
            width: "15%",
          },
          { key: "propertyFullName", title: "物件名・部屋番号", width: "15%" },
          {
            key: "approvalTime",
            title: "申請日時",
            render(data) {
              return data ? dayjs(data).format("YYYY/MM/DD HH:mm") : "未定"
            },
            width: "20%",
          },
          { key: "approvalUserName", title: "申請者", width: "15%" },
          {
            key: "meetingDate",
            title: "重説予定日時",
            render(data) {
              return data ? dayjs(data).format("YYYY/MM/DD HH:mm") : "未定"
            },
            width: "20%",
          },
          {
            key: "status",
            title: "ステータス",
            render(data) {
              return <TaskTag status={data} size={"fix"} />
            },
            width: "15%",
          },
        ],
      },
      contractsSignature: {
        title: "未サイン",
        theme: "light",
        headers: [
          {
            key: "name",
            title: "契約者名",
            render(data, alldata) {
              return (
                <Link
                  href={{
                    pathname: `/contracts${alldata.version === 2 ? "-next" : ""}/[id]/`,
                    query: { id: alldata.id },
                  }}
                >
                  {data}
                </Link>
              )
            },
            width: "25%",
          },
          { key: "propertyFullName", title: "物件名・部屋番号", width: "50%" },

          {
            key: "status",
            title: "ステータス",
            render(data) {
              return <TaskTag status={data} size={"fix"} />
            },
            width: "25%",
          },
        ],
      },
    }
  }, [])
  const [summary, setSummary] = useState<Summary>(null)
  const [agentSummary, setAgentSummary] = useState<AgentSummary>(null)
  const links = [
    { name: "契約準備中", status: 0 },
    { name: "重説準備中", status: 10 },
    { name: "重説準備完了", status: 20 },
    { name: "IT重説完了", status: 21 },
    // { name: "契約済", status: 30 },
  ]

  const load = async () => {
    if (!checkLoading.current) {
      try {
        loading(true)
        const [summaryData, activityData, taskData] = await Promise.all([
          getSummaryData(),
          getActivityData(),
          getTaskData(),
        ])
        setSummary(summaryData.data)
        if (authorization?.user?.company?.type === CompanyType.AGENT) {
          const { data } = await getAgentSummaryData()
          setAgentSummary(data)
        }
        setActivityData(
          activityData.data.map((item) => {
            let message = ""

            if (item.type === 1) {
              message = `様の「${item.data.workflow.name}」が登録されました。`
            } else if (item.type === 2) {
              message = `様の「${item.data.workflow.name}」が削除されました。`
            } else if (item.type === 3) {
              message = `様の「${contractStatus[item.data.status.from]}」から「${contractStatus[item.data.status.to]
                }」へ変更されました。`
            }

            return {
              id: item.contractId,
              name: item.data.name,
              message,
              date: item.createdAt,
              from: item.userName,
              version: item.version,
            }
          })
        )
        setTaskData(
          taskData.data.map((item) => {
            let message = ""
            // let status = item.status
            if (item.type === 0 || item.type === 1) {
              message = `様の${item.workflowName}を登録してください。`
            } else if (item.type === 2) {
              message = `様の${item.workflowName}を送信してください。`
              // status = 10
            } else if (item.type === 3) {
              message = `様の${item.workflowName}を受領確認してください。`
              // status = 10
            } else if (item.type === 4) {
              message = `様の${item.workflowName}を電子サインしてください。`
              // status = 10
            } else if (item.type === 99) {
              message = `様の${item.workflowName}の契約を完了してください。`
              // status = 30
            }

            return {
              id: item.id,
              name: item.name,
              message,
              status: item.status,
              version: item.version,
            }
          })
        )
      } catch (error) {
        showDialog("エラー", error.message, "閉じる")
      } finally {
        loading(false)
      }
    }
    checkLoading.current = false
  }

  const getFormDefault = async () => {
    const { data } = await getDefaultForm()
    const isRegister = data.every((item) => item.updatedAt)
    dispatch(setIsRegisterTemplateForm(isRegister))
  }

  useEffect(() => {
    clearForm()

    load()
    getFormDefault()
    socket?.notify((data) => {
      console.log("--------リロード...")
      load()
    })
    return () => {
      socket?.unNotify()
    }
  }, [])

  const onMove = (index: number) => {
    const status = tabData[index]?.status
    if (status === 50) {
      router.push({ pathname: "/contracts/cancel/" })
    } else if (status === 40) {
      router.push({ pathname: "/contracts/complete/" })
    } else if (typeof tabData[index]?.status === "number") {
      router.push({ pathname: "/contracts/progress/", query: { status: tabData[index]?.status } })
    } else {
      router.push({ pathname: "/contracts/progress/" })
    }
  }

  const onKeyup = (e) => {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      if (e.target.value) {
        router.push(`/contracts/search/${e.target.value}`)
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <Content className={"back-gray"}>
        <ContentInner>
          <ButtonHeader>
            <AcInput
              onKeyup={onKeyup}
              name="text"
              type="keyword"
              placeholder="契約番号、契約者名、物件名で検索"
              className="w-25"
            />
            {authorization.user?.company?.type === CompanyType.MANAGEMENT && <ModalImportCSV />}
          </ButtonHeader>
        </ContentInner>
        {summary ? (
          <ContentInner className={"back-white"}>
            <LinkBoxes>
              {links.map((link, index) => (
                <AcButton onClick={onMove.bind(this, index)} key={index}>
                  <div>
                    {link.name}
                    <div>
                      ({new Intl.NumberFormat("ja-JP").format(summary[link.status]?.count || 0)})
                    </div>
                  </div>
                </AcButton>
              ))}
              <AcButton onClick={onMove.bind(this, null)}>
                <div>全ての案件</div>
              </AcButton>
            </LinkBoxes>
            {authorization?.user?.company?.type === "MANAGEMENT" ? (
              <ListViews>
                {/*契約準備中*/}
                <ListView
                  {...tableHeaders[0]}
                  dataSources={summary[0]?.list}
                  rightHref={{ pathname: "/contracts/progress/", query: { status: 0 } }}
                />

                {/*未完了の個人タスク*/}
                <TaskView title={"未完了の個人タスク"} dataSources={taskData} />

                {/*契約済*/}
                {/* <ListView
                  {...tableHeaders[30]}
                  dataSources={summary[30].list}
                  rightHref={{ pathname: "/contracts/progress/", query: { status: 30 } }}
                /> */}

                {/*最近のアクティビティ*/}
                <ActivityView title={"最近のアクティビティ"} dataSources={activityData} />

                {/*契約完了*/}
                <ListView
                  {...tableHeaders[40]}
                  dataSources={summary[40]?.list}
                  rightHref={{ pathname: "/contracts/complete/" }}
                />

                {/*キャンセル*/}
                {/* <ListView
                  {...tableHeaders[50]}
                  dataSources={summary[50].list}
                  rightHref={{ pathname: "/contracts/cancel/" }}
                /> */}
              </ListViews>
            ) : (
              <ListViews>
                {agentSummary ? (
                  <>
                    <ListView
                      {...agentTableHeaders.contractsConfirmation}
                      dataSources={agentSummary.contractsConfirmation}
                    />
                    <ListView
                      {...agentTableHeaders.contractsSignature}
                      dataSources={agentSummary.contractsSignature}
                    />
                    <ListView
                      {...agentTableHeaders.contractsRequest}
                      dataSources={agentSummary.contractsRequest}
                      fulWidth
                    />
                  </>
                ) : (
                  <AcLoading isOverlay={true} />
                )}
              </ListViews>
            )}
          </ContentInner>
        ) : (
          <AcLoading isOverlay={true} />
        )}
      </Content>
    </FormProvider>
  )
}
const Content = styled.div`
  background-color: #f6f9fc;

  > .back-white {
    background-color: #ffffff;
    padding: 20px;
  }

  .display-block {
    display: block !important;
  }
`

const LinkBoxes = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;

  > button.btn,
  button.btn:hover {
    color: #222222;
    width: 270px;
    height: 88px;
    margin: 0px 0px 27px;
    padding: 20px 34px;
    border-radius: 10px;
    background-color: #e5f1ff;
    letter-spacing: 0.05em;
    line-height: 24px;

    > div {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      font-size: 20px;

      > span {
        font-size: 26px;
      }
    }

    > div:last-child {
      font-weight: bold;
      font-size: 20px;
      color: #6b6bf8;
    }

    &:last-child > div {
      justify-content: center;
    }
  }
`

const ListViews = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: stretch;

  > div {
    margin-bottom: 30px;
  }
`
const ButtonHeader = styled.div`
  display: flex;
  justify-content: end;
  padding: 20px;
  align-items: center;

  .resister-btn {
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.12em;
    line-height: 24px;
    margin-left: 15px;

    i {
      margin-right: 10px;
    }
  }
`
